import React from 'react';
import { useQuery } from 'react-query';
import { FiArrowLeft } from 'react-icons/fi';
import { getCommands } from '../../services/commandService';
import { commandTypeName, commandStatusMeta, fmtTime } from '../../utils/format';

const STATUS_FILTERS = [
  { key: '', name: '全部' },
  { key: 'pending', name: '排队中' },
  { key: 'picked', name: '已拉取' },
  { key: 'executed', name: '已执行' },
  { key: 'failed', name: '执行失败' },
  { key: 'expired', name: '已过期' },
  { key: 'cancelled', name: '已取消' }
];

const CommandHistory = () => {
  const [status, setStatus] = React.useState('');
  const [page, setPage] = React.useState(1);

  const { data, isLoading, error } = useQuery(
    ['commands', 'history', status, page],
    () => getCommands({ status, page, limit: 15 }),
    { keepPreviousData: true }
  );

  const commands = data?.commands || [];

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center gap-3">
        <a href="/commands" onClick={(e) => { e.preventDefault(); window.history.back(); }} className="text-[#57606a] hover:text-[#1f2328]">
          <FiArrowLeft className="text-xl" />
        </a>
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">执行记录</h1>
          <p className="text-[#656d76] text-sm mt-1">所有远程指令的下发与执行历史（含审计）</p>
        </div>
      </div>

      {/* 状态过滤 */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => { setStatus(f.key); setPage(1); }}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              status === f.key
                ? 'bg-[#ddf4ff] text-[#0969da] border-[#b6e3ff] font-medium'
                : 'bg-white text-[#57606a] border-[#d0d7de] hover:bg-[#f6f8fa]'
            }`}
          >
            {f.name}
          </button>
        ))}
      </div>

      {/* 指令表格 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="px-5 py-16 text-center text-[#656d76] text-sm">加载中...</div>
        ) : error ? (
          <div className="px-5 py-16 text-center text-[#cf222e] text-sm">获取指令列表失败</div>
        ) : commands.length === 0 ? (
          <div className="px-5 py-16 text-center text-[#656d76] text-sm">暂无指令记录</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f6f8fa] text-[#656d76] text-xs">
                  <th className="text-left px-4 py-3 font-medium">ID</th>
                  <th className="text-left px-4 py-3 font-medium">指令</th>
                  <th className="text-left px-4 py-3 font-medium hidden md:table-cell">授权码</th>
                  <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">目标</th>
                  <th className="text-left px-4 py-3 font-medium">状态</th>
                  <th className="text-left px-4 py-3 font-medium hidden md:table-cell">下发人</th>
                  <th className="text-left px-4 py-3 font-medium">下发时间</th>
                  <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">完成时间</th>
                  <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">回执</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eaeef2]">
                {commands.map(cmd => {
                  const meta = commandStatusMeta(cmd.status);
                  return (
                    <tr key={cmd.id} className="hover:bg-[#f6f8fa]">
                      <td className="px-4 py-3 text-[#57606a] font-mono text-xs">#{cmd.id}</td>
                      <td className="px-4 py-3 text-[#1f2328]">{commandTypeName(cmd.commandType)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#1f2328] hidden md:table-cell">{cmd.licenseKey}</td>
                      <td className="px-4 py-3 text-[#57606a] text-xs hidden lg:table-cell">
                        {cmd.instanceId ? cmd.instanceId.slice(0, 18) + '...' : '全部实例'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${meta.cls}`}>{meta.name}</span>
                      </td>
                      <td className="px-4 py-3 text-[#57606a] hidden md:table-cell">
                        {cmd.creator?.username || `#${cmd.createdBy}`}
                      </td>
                      <td className="px-4 py-3 text-[#656d76] text-xs">{fmtTime(cmd.createdAt)}</td>
                      <td className="px-4 py-3 text-[#656d76] text-xs hidden lg:table-cell">{fmtTime(cmd.executedAt)}</td>
                      <td className="px-4 py-3 text-[#57606a] text-xs hidden lg:table-cell max-w-[200px] truncate" title={cmd.resultMessage || ''}>
                        {cmd.resultMessage || (cmd.errorCode ? `错误码 ${cmd.errorCode}` : '-')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 分页 */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#656d76] text-xs">
            共 {data.totalItems} 条 · 第 {data.currentPage}/{data.totalPages} 页
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-xs border border-[#d0d7de] rounded-md bg-white hover:bg-[#f6f8fa] disabled:opacity-40"
            >
              上一页
            </button>
            <button
              onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
              disabled={page >= data.totalPages}
              className="px-3 py-1.5 text-xs border border-[#d0d7de] rounded-md bg-white hover:bg-[#f6f8fa] disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandHistory;
